import { Component, computed, inject, signal } from '@angular/core'
import { form, FormField, FormRoot, validateStandardSchema } from '@angular/forms/signals'
import { MatButtonModule } from '@angular/material/button'
import { MatDialogModule } from '@angular/material/dialog'
import { MatFormFieldModule } from '@angular/material/form-field'
import { MatIconModule } from '@angular/material/icon'
import { MatInputModule } from '@angular/material/input'
import { EMAIL_CHANGE_LINK_HOURS, SEIRI_SUPPORT_EMAIL } from '@seiri/shared/common'
import { DateTime } from 'luxon'
import { AuthApiService, UsersApiService } from '../../api'
import { FieldErrorDirective } from '../../directives/field-error.directive'
import { LoadingButtonDirective } from '../../directives/loading-button.directive'
import { ChangeEmailFormModel, changeEmailFormModel, toRequestEmailChangeBody } from '../../forms/auth-form.model'
import { describeApiError } from '../../i18n/describe-error'
import { ACCOUNT_HOST } from './account-host'

/**
 * Zmeniť e-mail, opened from Môj profil (docs/account.md). Asks for the new address and the current password, then
 * says where the link went; the account changes only when it is opened. When the yearly limit is used up it says so,
 * with the date and the support address, instead of offering the form.
 */
@Component({
  selector: 'seiri-change-email-dialog',
  imports: [
    FieldErrorDirective,
    FormField,
    FormRoot,
    LoadingButtonDirective,
    MatButtonModule,
    MatDialogModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
  ],
  template: `
    <div class="dialog-header">
      <h2 mat-dialog-title>Zmeniť e-mail na prihlásenie</h2>
      <button type="button" mat-icon-button mat-dialog-close aria-label="Zavrieť"><mat-icon>close</mat-icon></button>
    </div>
    @if (sentTo(); as sentTo) {
      <mat-dialog-content class="dialog-body">
        <div class="callout callout--success" role="status">
          <mat-icon aria-hidden="true">mark_email_read</mat-icon>
          <span>
            Poslali sme odkaz na <strong>{{ sentTo }}</strong
            >. E-mail sa zmení, keď ho otvoríte. Odkaz platí {{ linkHours }} hodín.
          </span>
        </div>
      </mat-dialog-content>
      <mat-dialog-actions align="end">
        <button type="button" mat-raised-button color="primary" mat-dialog-close>Rozumiem</button>
      </mat-dialog-actions>
    } @else if (nextAllowedOn(); as nextAllowedOn) {
      <mat-dialog-content class="dialog-body">
        <div class="callout callout--warning" role="status">
          <mat-icon aria-hidden="true">schedule</mat-icon>
          <span>
            E-mail na prihlásenie ste za posledný rok zmenili už dvakrát. Ďalšia zmena bude možná od
            {{ nextAllowedOn }}. Ak ju potrebujete skôr, napíšte nám na
            <a [href]="'mailto:' + supportEmail">{{ supportEmail }}</a
            >.
          </span>
        </div>
      </mat-dialog-content>
      <mat-dialog-actions align="end">
        <button type="button" mat-button mat-dialog-close>Zavrieť</button>
      </mat-dialog-actions>
    } @else {
      <form [formRoot]="form">
        <mat-dialog-content class="dialog-body">
          <p>
            Na novú adresu pošleme odkaz. Kým ho neotvoríte, prihlasujete sa súčasnou. Za rok ju môžete zmeniť
            {{ remaining() === 1 ? 'už len raz' : 'dvakrát' }}.
          </p>
          <mat-form-field appearance="outline">
            <mat-label>Nový e-mail</mat-label>
            <input matInput type="email" autocomplete="email" [formField]="form.newEmail" />
            <mat-error [seiriFieldError]="form.newEmail" />
          </mat-form-field>
          <mat-form-field appearance="outline">
            <mat-label>Súčasné heslo</mat-label>
            <input matInput type="password" autocomplete="current-password" [formField]="form.currentPassword" />
            <mat-error [seiriFieldError]="form.currentPassword" />
          </mat-form-field>

          @if (error()) {
            <div class="callout callout--danger" role="alert">
              <mat-icon aria-hidden="true">error_outline</mat-icon><span>{{ error() }}</span>
            </div>
          }
        </mat-dialog-content>
        <mat-dialog-actions align="end">
          <button type="button" mat-button mat-dialog-close>Zrušiť</button>
          <button type="submit" mat-raised-button color="primary" [appLoadingButton]="form().submitting()">
            Poslať odkaz
          </button>
        </mat-dialog-actions>
      </form>
    }
  `,
})
export class ChangeEmailDialogComponent {
  private readonly host = inject(ACCOUNT_HOST)
  private readonly authApi = inject(AuthApiService)
  private readonly usersApi = inject(UsersApiService)

  protected readonly supportEmail = SEIRI_SUPPORT_EMAIL
  protected readonly linkHours = EMAIL_CHANGE_LINK_HOURS
  protected readonly remaining = computed(() => this.host.user()?.emailChange.remaining ?? 0)
  /** The person's own date, in their browser's zone: an account is not a premise's. */
  protected readonly nextAllowedOn = computed(() => {
    const next = this.host.user()?.emailChange.nextAllowedAt
    return this.remaining() === 0 && next ? DateTime.fromISO(next).setLocale('sk').toFormat('d. M. yyyy') : ''
  })

  protected readonly sentTo = signal('')
  protected readonly error = signal('')
  protected readonly model = signal<ChangeEmailFormModel>({
    newEmail: this.host.user()?.pendingEmail ?? '',
    currentPassword: '',
  })
  protected readonly form = form(this.model, (path) => validateStandardSchema(path, changeEmailFormModel), {
    submission: {
      action: async (field) => {
        this.error.set('')
        const body = toRequestEmailChangeBody(changeEmailFormModel.parse(field().value()))
        try {
          await this.authApi.requestEmailChange(body)
          this.sentTo.set(body.newEmail)
        } catch (error) {
          this.error.set(describeApiError(error, 'Odkaz sa nepodarilo poslať'))
          return undefined
        }
        // The waiting address changed; a failed refresh only leaves the page a step behind until the next load.
        const refreshed = await this.usersApi.me().catch(() => null)
        if (refreshed) this.host.setUser(refreshed)
        return undefined
      },
    },
  })
}
